import { fetchAuthSession } from "aws-amplify/auth";
import type { FetchError } from "./types.js";

// ── Analytics: api_error dispatcher ────────────────────────
//
// We dispatch DOM CustomEvents instead of importing a UI/analytics package
// directly so this Tier-3 client stays free of cross-tier deps. Apps install
// a listener (typically inside an analytics provider component) and forward
// the events to the active tracker.

const API_ERROR_EVENT = "cakewalk:api-error";
const ANALYTICS_EVENT = "cakewalk:analytics-event";

function dispatchApiError(params: {
  endpoint: string;
  method?: string;
  error_code: string;
  status?: number;
}): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(API_ERROR_EVENT, { detail: params }));
  } catch {
    // never let analytics crash an API call
  }
}

function dispatchAnalyticsEvent(eventName: string, params: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent(ANALYTICS_EVENT, {
        detail: { eventName, params },
      }),
    );
  } catch {
    // never let analytics crash an API call
  }
}

function safeUrlPath(url: string): string {
  try {
    return new URL(url, typeof window !== "undefined" ? window.location.origin : "http://localhost")
      .pathname;
  } catch {
    return url;
  }
}

// ── Retry configuration ────────────────────────────────────

const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 1000,
} as const;

/** 429 (rate limit) and 5xx (server errors) retry; everything else does not. */
function isRetryableStatus(status: number): boolean {
  if (status === 429) return true;
  if (status >= 500 && status < 600) return true;
  return false;
}

/** Honors `Retry-After` when present, otherwise exponential backoff (1s, 2s, 4s). */
function getRetryDelay(attempt: number, response?: Response): number {
  const retryAfter = response?.headers.get("Retry-After");
  if (retryAfter) {
    const seconds = Number.parseInt(retryAfter, 10);
    if (!Number.isNaN(seconds)) return seconds * 1000;
  }
  return 2 ** attempt * RETRY_CONFIG.baseDelayMs;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ── API client ─────────────────────────────────────────────

export interface ApiClient {
  get: <T = unknown>(path: string, options?: RequestInit) => Promise<T>;
  post: <T = unknown>(path: string, body?: unknown) => Promise<T>;
  put: <T = unknown>(path: string, body?: unknown) => Promise<T>;
  del: <T = unknown>(path: string) => Promise<T>;
  request: <T = unknown>(path: string, options?: RequestInit) => Promise<T>;
}

export interface ApiClientOptions {
  /** Base URL for all requests (e.g. `https://api.cakewalk.com`). */
  readonly baseUrl?: string;
  /** Pre-fetched ID token; skips the first Amplify session lookup. Ignored when `requireAuth: false`. */
  readonly initialToken?: string;
  /**
   * Whether to attach a Cognito ID token to every request.
   *
   * - `true` (default) — pull the token from `aws-amplify/auth.fetchAuthSession`
   *   on each request, attach it as `Authorization: Bearer …`, and on 401
   *   force-refresh once before signing out. This is the right shape for
   *   authenticated SPA surfaces (cockpit, wallet, partners, admin,
   *   onboarding).
   * - `false` — never fetch a session, never attach the header, and let
   *   401s surface as regular `FetchError`s. The right shape for the
   *   platform's genuinely public endpoints (owner-registration, activation,
   *   places autocomplete, partner-auth config) where there is no Cognito
   *   session yet. See ADR-0013.
   */
  readonly requireAuth?: boolean;
}

function normalizeOptions(
  arg1: ApiClientOptions | string | undefined,
  arg2: string | undefined,
): Required<Pick<ApiClientOptions, "requireAuth">> & Omit<ApiClientOptions, "requireAuth"> {
  if (typeof arg1 === "object") {
    return {
      baseUrl: arg1.baseUrl,
      initialToken: arg1.initialToken,
      requireAuth: arg1.requireAuth ?? true,
    };
  }
  return {
    baseUrl: arg1,
    initialToken: arg2,
    requireAuth: true,
  };
}

/**
 * Construct an API client. Two call signatures are supported:
 *
 *   createApiClient({ baseUrl, requireAuth })
 *   createApiClient(baseUrl, initialToken)        // back-compat
 *
 * Behaviour:
 *
 * - **Authenticated mode (`requireAuth: true`, default)** — pulls the ID
 *   token from Amplify on each request, deduplicating concurrent refresh
 *   attempts. On 401, force-refreshes the token once and retries; if still
 *   401, calls `signOut()` and navigates to `/`.
 * - **Public mode (`requireAuth: false`)** — never calls Amplify; never
 *   attaches `Authorization`. 401s surface as `FetchError` like any other
 *   4xx; the client does not sign anyone out. Per ADR-0013, this is the
 *   right mode for the platform's pre-login endpoints (owner-registration,
 *   activation, places, partner-auth).
 * - **Both modes** — on 429 / 5xx / network errors, retries up to 3 times
 *   with `Retry-After` support and exponential backoff. On 422 with a
 *   structured `errors` array, throws a `FetchError` with
 *   `isEmulatorError = true`. Carries the `?emulateEmail=…` query
 *   parameter forward (and `dbOnly=true` when present).
 */
export function createApiClient(options?: ApiClientOptions): ApiClient;
export function createApiClient(baseUrl?: string, initialToken?: string): ApiClient;
export function createApiClient(arg1?: ApiClientOptions | string, arg2?: string): ApiClient {
  const opts = normalizeOptions(arg1, arg2);
  const baseUrl = opts.baseUrl;
  const requireAuth = opts.requireAuth;

  let tokenRefreshPromise: Promise<string> | null = null;

  async function getFreshToken(forceRefresh = false): Promise<string> {
    if (tokenRefreshPromise) return tokenRefreshPromise;

    tokenRefreshPromise = (async () => {
      try {
        const session = await fetchAuthSession({ forceRefresh });
        if (!session.tokens?.idToken) {
          throw new Error("No ID token found in Amplify session");
        }
        return session.tokens.idToken.toString();
      } finally {
        tokenRefreshPromise = null;
      }
    })();

    return tokenRefreshPromise;
  }

  function applyEmulatorParams(path: string): string {
    if (typeof window === "undefined") return path;
    const currentParams = new URLSearchParams(window.location.search);
    const emulateEmail = currentParams.get("emulateEmail");
    if (!emulateEmail) return path;

    const sep = path.includes("?") ? "&" : "?";
    let next = `${path}${sep}emulateEmail=${encodeURIComponent(emulateEmail)}`;
    const dbOnly = currentParams.get("dbOnly") ?? currentParams.get("hubspotOnly");
    if (dbOnly === "true") next = `${next}&dbOnly=true`;
    return next;
  }

  async function parseEmulator422(res: Response): Promise<never> {
    const payload = (await res.json()) as {
      error?: string;
      errors?: Array<{
        code: string;
        message: string;
        impact: string;
        fix: string;
      }>;
      warnings?: unknown;
      details?: unknown;
      suggestion?: string;
      message?: string;
    };

    if (payload.errors && Array.isArray(payload.errors)) {
      const fetchError = new Error(payload.error ?? "Emulator validation failed") as FetchError;
      fetchError.info = payload;
      fetchError.status = 422;
      fetchError.isEmulatorError = true;
      throw fetchError;
    }

    const fetchError = new Error(payload.error ?? payload.message ?? "HTTP 422") as FetchError;
    fetchError.info = payload;
    fetchError.status = 422;
    throw fetchError;
  }

  async function parseGenericError(res: Response): Promise<never> {
    const payload = (await res.json().catch(() => ({}))) as {
      error?: string;
      detail?: string;
      message?: string;
      [k: string]: unknown;
    };
    const msg = payload.detail ?? payload.error ?? payload.message ?? `HTTP ${res.status}`;
    const fetchError = new Error(msg) as FetchError;
    fetchError.info = payload;
    fetchError.status = res.status;
    throw fetchError;
  }

  async function parseJson<T>(res: Response): Promise<T> {
    try {
      return (await res.json()) as T;
    } catch (detail) {
      throw Object.assign(new Error("Invalid JSON"), { detail }) as FetchError;
    }
  }

  function buildHeaders(authToken: string | null, options: RequestInit): HeadersInit {
    const h: Record<string, string> = {
      ...((options.headers as Record<string, string>) ?? {}),
    };
    if (authToken) {
      h.Authorization = `Bearer ${authToken}`;
    }
    // `post`/`put` pass `JSON.stringify(body)` — a STRING — so the JSON
    // content type must be set for string bodies (the old `typeof body ===
    // "object"` check never matched, the browser defaulted to text/plain,
    // and Hono's zod-openapi json validator silently saw an empty body).
    // FormData stays untouched so the browser writes the multipart boundary;
    // an explicit caller-provided Content-Type always wins.
    const hasContentType = Object.keys(h).some((k) => k.toLowerCase() === "content-type");
    if (!hasContentType && options.body && typeof options.body === "string") {
      h["Content-Type"] = "application/json";
    }
    return h;
  }

  async function request<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
    const fullPath = applyEmulatorParams(path);

    // Public mode: never call Amplify, never attach Authorization, never
    // intercept 401s. Public endpoints (owner-registration, activation,
    // places, partner-auth) are intentionally callable without a Cognito
    // session — see ADR-0013.
    let token: string | null = null;
    if (requireAuth) {
      token = opts.initialToken ?? null;
      if (!token) {
        try {
          token = await getFreshToken(false);
        } catch {
          throw new Error("Unable to authenticate — please ensure you are logged in");
        }
      }
    }

    const url = baseUrl ? `${baseUrl}${fullPath}` : fullPath;

    let lastError: Error | undefined;

    for (let attempt = 0; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
      try {
        const res = await fetch(url, {
          ...options,
          headers: buildHeaders(token, options),
        });

        // 401 handling — authenticated mode only. In public mode a 401
        // means "your input is wrong" (e.g. expired activation token), not
        // "your Cognito session expired"; surface it as a regular error.
        if (res.status === 401 && requireAuth) {
          const freshToken = await getFreshToken(true);
          token = freshToken;
          const retryRes = await fetch(url, {
            ...options,
            headers: buildHeaders(freshToken, options),
          });

          if (retryRes.status === 401) {
            dispatchAnalyticsEvent("session_expired", {
              endpoint: safeUrlPath(url),
            });
            const { signOut } = await import("aws-amplify/auth");
            await signOut();
            if (typeof window !== "undefined") {
              window.location.href = "/";
            }
            throw new Error("Unauthorized — please log in again");
          }

          if (retryRes.status === 304) return {} as T;
          if (retryRes.status === 422) await parseEmulator422(retryRes);
          if (!retryRes.ok) await parseGenericError(retryRes);
          return await parseJson<T>(retryRes);
        }

        if (isRetryableStatus(res.status)) {
          if (attempt < RETRY_CONFIG.maxRetries) {
            await sleep(getRetryDelay(attempt, res));
            continue;
          }
          dispatchAnalyticsEvent("api_retry_exhausted", {
            endpoint: safeUrlPath(url),
            attempts: RETRY_CONFIG.maxRetries + 1,
          });
        }

        if (res.status === 304) return {} as T;
        if (res.status === 422) await parseEmulator422(res);

        if (!res.ok) {
          dispatchApiError({
            endpoint: safeUrlPath(url),
            method: (options.method ?? "GET").toUpperCase(),
            error_code: String(res.status),
            status: res.status,
          });
          await parseGenericError(res);
        }

        return await parseJson<T>(res);
      } catch (error) {
        lastError = error as Error;

        if (error instanceof TypeError && error.message.includes("Failed to fetch")) {
          if (attempt < RETRY_CONFIG.maxRetries) {
            await sleep(getRetryDelay(attempt));
            continue;
          }
          dispatchAnalyticsEvent("api_retry_exhausted", {
            endpoint: safeUrlPath(url),
            attempts: RETRY_CONFIG.maxRetries + 1,
            error_type: "network",
          });
          throw new Error("Network error — please check your connection");
        }

        throw error;
      }
    }

    throw lastError ?? new Error("Unexpected error — request never completed");
  }

  return {
    request,
    get: <T = unknown>(path: string, options: RequestInit = {}) =>
      request<T>(path, { ...options, method: "GET" }),
    post: <T = unknown>(path: string, body?: unknown) =>
      request<T>(path, {
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
      }),
    put: <T = unknown>(path: string, body?: unknown) =>
      request<T>(path, {
        method: "PUT",
        body: body ? JSON.stringify(body) : undefined,
      }),
    del: <T = unknown>(path: string) => request<T>(path, { method: "DELETE" }),
  };
}
